import { useEffect, useState } from 'react';
import {
	Button,
	Form,
	Input,
	Select,
	Row,
	Col,
	Dropdown,
	Menu,
	notification,
	Tooltip as AntTooltip,
} from 'antd';
import {
	MinusCircleOutlined,
	PlusOutlined,
	DownOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { updatePostCallAnalysis } from '@/api/logs';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUser } from '@/api/user';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';

type AnalysisCategory = 'Text' | 'JSON' | 'Boolean' | 'Number';

interface PostAnalysisSegment {
	label: string;
	variableName: string;
	description: string;
	example: string;
	analysisCategory: AnalysisCategory;
}

const templates: PostAnalysisSegment[] = [
	{
		label: 'Customer Feedback',
		variableName: 'customer_feedback',
		description: 'Analyze customer feedback to detect sentiment and keywords.',
		example: "{'sentiment': 'positive', 'keywords': ['service', 'pricing']}",
		analysisCategory: 'Text',
	},
	{
		label: 'Sales Data',
		variableName: 'sales_data',
		description: 'Analyze sales-related calls to extract key metrics.',
		example: "{'total_sales': 5000, 'region': 'North America'}",
		analysisCategory: 'Number',
	},
	{
		label: 'Call Classification',
		variableName: 'call_classification',
		description: 'Classify calls into predefined categories.',
		example: "{'category': 'Support', 'priority': 'High'}",
		analysisCategory: 'JSON',
	},
	{
		label: 'Call Outcome',
		variableName: 'call_outcome',
		description: 'Determine the outcome of the call as success or failure.',
		example: "{'outcome': true}",
		analysisCategory: 'Boolean',
	},
];

// Enhanced explanations for data formats with detailed examples
const dataTypeExplanations = {
	Text: 'Simple text responses like summaries or comments',
	JSON: 'Structured information with multiple fields',
	Boolean: 'Yes/No answers only (true/false)',
	Number: 'Numerical values like amounts or counts',
};

// Format examples for each data type - these will be shown in the disabled field
const formatExamples = {
	Text: 'Summary: "Customer expressed satisfaction with our service but had concerns about pricing."',
	JSON: '{"sentiment": "positive", "topics": ["pricing", "features"], "follow_up_required": true}',
	Boolean: 'true (for yes) or false (for no)',
	Number: '{"call_duration": 320, "quote_amount": 499.99, "items_discussed": 5}',
};

const PostCallAnalysis = () => {
	const [segments, setSegments] = useState<PostAnalysisSegment[]>([]);
	const [availableTemplates, setAvailableTemplates] = useState(templates);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const { data, refetch } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId as string),
		enabled: !!selectedAgentId,
	});

	useEffect(() => {
		if (data?.post_call_analysis && Array.isArray(data.post_call_analysis)) {
			// Create new objects to ensure they're mutable
			const loadedSegments = data.post_call_analysis.map((segment: any) => ({
				label: segment.label || '',
				variableName: segment.variableName || '',
				description: segment.description || '',
				example: segment.example || '',
				analysisCategory: segment.analysisCategory || 'Text',
			}));

			setSegments(loadedSegments);

			// Update available templates based on what's already loaded
			const existingLabels = loadedSegments.map((segment) => segment.label);
			const filteredTemplates = templates.filter(
				(template) => !existingLabels.includes(template.label),
			);
			setAvailableTemplates(filteredTemplates);
		}
	}, [data]);

	const { mutate: updatePostCallAnalysisMutation, status } = useMutation({
		mutationKey: ['updatePostCallAnalysis'],
		mutationFn: updatePostCallAnalysis,
		onSuccess() {
			notification.success({ message: 'Call analysis saved successfully!' });
			refetch();
		},
		onError() {
			notification.error({ message: 'Unable to save call analysis' });
		},
	});

	// Derive loading state from status
	const isLoading = status === 'pending';

	const handleSaveAnalysis = () => {
		// Validate required fields before saving - now ALL fields are required
		const hasEmptyFields = segments.some(
			(segment) =>
				!segment.label ||
				!segment.variableName ||
				!segment.description ||
				!segment.example ||
				!segment.analysisCategory,
		);

		if (hasEmptyFields) {
			notification.warning({ message: 'Please fill in all required fields before saving.' });
			return;
		}

		updatePostCallAnalysisMutation({
			postCallAnalysis: segments,
			agentId: selectedAgentId as string,
		});
	};

	const handleAddSegment = () => {
		setSegments((prevSegments) => [
			...prevSegments,
			{ label: '', variableName: '', description: '', example: '', analysisCategory: 'Text' },
		]);
	};

	const handleRemoveSegment = (index: number) => {
		const removedSegment = segments[index];
		setSegments((prevSegments) => prevSegments.filter((_, i) => i !== index));

		// If the segment was from a template, add it back to available templates
		if (templates.some((t) => t.label === removedSegment.label)) {
			setAvailableTemplates((prevTemplates) => [...prevTemplates, removedSegment]);
		}
	};

	const handleChange = (index: number, field: keyof PostAnalysisSegment, value: any) => {
		setSegments((prevSegments) => {
			// Create a new array of segments
			const updatedSegments = [...prevSegments];
			// Create a new object for the segment we want to modify
			updatedSegments[index] = {
				...updatedSegments[index],
				[field]: value,
			};
			return updatedSegments;
		});
	};

	const handleSelectTemplate = (template: PostAnalysisSegment) => {
		setSegments((prevSegments) => [...prevSegments, { ...template }]);
		setAvailableTemplates((prevTemplates) =>
			prevTemplates.filter((t) => t.label !== template.label),
		);
	};

	const templateMenu = (
		<Menu>
			{availableTemplates.map((template, index) => (
				<Menu.Item key={index} onClick={() => handleSelectTemplate(template)}>
					{template.label}
				</Menu.Item>
			))}
			{availableTemplates.length === 0 && (
				<Menu.Item disabled>No more templates available</Menu.Item>
			)}
		</Menu>
	);

	return (
		<div className="p-4 bg-white rounded-lg shadow">
			<div className="mb-4 flex justify-between items-center">
				<h3 className="text-lg font-medium">Smart Call Analysis Setup</h3>
				<Button type="primary" onClick={handleSaveAnalysis} loading={isLoading}>
					Save Setup
				</Button>
			</div>

			<div className="mb-4">
				<p className="text-sm text-gray-600 mb-2">
					Choose what information to collect from your calls. Add up to 3 items.
				</p>
			</div>

			{/* Simplified template selection */}
			<div className="mb-4 flex space-x-2">
				<Dropdown
					overlay={templateMenu}
					disabled={availableTemplates.length === 0 || segments.length >= 3}
				>
					<Button>
						Select Template <DownOutlined />
					</Button>
				</Dropdown>

				<Button
					type="dashed"
					name="customSegmentButton"
					data-testid="customSegmentButton"
					icon={<PlusOutlined />}
					onClick={handleAddSegment}
					disabled={segments.length >= 3}
				>
					Add Custom
				</Button>
			</div>

			{segments.length === 0 && (
				<div className="text-center p-4 bg-gray-50 rounded mb-4">
					<p className="text-gray-500">
						No items added yet. Choose a template or add a custom item to get started.
					</p>
				</div>
			)}

			<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
				{segments.map((segment, index) => (
					<div
						key={index}
						className="border rounded border-gray-300"
						style={{ marginBottom: '16px', padding: '16px' }}
					>
						<div className="border-b border-gray-200 pb-2 mb-4 flex justify-between items-center">
							<span className="font-medium">Scenario #{index + 1}</span>
							<Button
								danger
								icon={<MinusCircleOutlined />}
								onClick={() => handleRemoveSegment(index)}
							>
								Remove
							</Button>
						</div>
						<Form layout="vertical">
							<Row gutter={16}>
								<Col span={24}>
									<Form.Item
										label={
											<div className="flex items-center">
												<span>What do you want to track?</span>
												<AntTooltip title="Define what information you want to extract from calls">
													<InfoCircleOutlined className="ml-2 text-gray-400" />
												</AntTooltip>
											</div>
										}
										required
									>
										<Input
											value={segment.label}
											placeholder="e.g., Customer Satisfaction, Sales Opportunity"
											onChange={(e) => handleChange(index, 'label', e.target.value)}
										/>
									</Form.Item>
								</Col>

								<Col span={24}>
									<Form.Item
										label={
											<div className="flex items-center">
												<span>Field Name</span>
												<AntTooltip title="This will be used in reports and data exports">
													<InfoCircleOutlined className="ml-2 text-gray-400" />
												</AntTooltip>
											</div>
										}
										required
									>
										<Input
											value={segment.variableName}
											placeholder="e.g., customer_satisfaction"
											onChange={(e) => handleChange(index, 'variableName', e.target.value)}
										/>
									</Form.Item>
								</Col>

								<Col span={24}>
									<Form.Item
										label={
											<div className="flex items-center">
												<span>What the AI will capture during calls</span>
												<AntTooltip title="Tell the AI what to listen for in conversations and what data to extract">
													<InfoCircleOutlined className="ml-2 text-gray-400" />
												</AntTooltip>
											</div>
										}
										required
									>
										<Input.TextArea
											rows={2}
											placeholder="e.g., Identify if the customer mentioned product issues"
											value={segment.description}
											onChange={(e) => handleChange(index, 'description', e.target.value)}
										/>
									</Form.Item>
								</Col>

								<Col span={24}>
									<Form.Item
										label={
											<div className="flex items-center">
												<span>Format of data</span>
												<AntTooltip title="Choose how the information will be structured in your reports">
													<InfoCircleOutlined className="ml-2 text-gray-400" />
												</AntTooltip>
											</div>
										}
										required
									>
										<Select
											value={segment.analysisCategory}
											onChange={(value) => handleChange(index, 'analysisCategory', value)}
											className="w-full"
										>
											{Object.entries(dataTypeExplanations).map(([type, explanation]) => (
												<Select.Option key={type} value={type}>
													{type} - {explanation}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
								</Col>

								{/* Format Example Field - Displayed but disabled, not included in payload */}
								<Col span={24}>
									<Form.Item
										label={
											<div className="flex items-center">
												<span>Output Format Example</span>
												<AntTooltip title="This shows how your data will be formatted">
													<InfoCircleOutlined className="ml-2 text-gray-400" />
												</AntTooltip>
											</div>
										}
									>
										<Input.TextArea
											disabled
											className="bg-gray-50 text-gray-500"
											rows={2}
											value={formatExamples[segment.analysisCategory]}
										/>
									</Form.Item>
								</Col>

								<Col span={24}>
									<Form.Item
										label={
											<div className="flex items-center">
												<span>Expected Output Format</span>
												<AntTooltip title="Provide an example of what the output will look like based on the format above">
													<InfoCircleOutlined className="ml-2 text-gray-400" />
												</AntTooltip>
											</div>
										}
										required
									>
										<Input.TextArea
											placeholder={'Enter your example following the format above'}
											value={segment.example}
											onChange={(e) => handleChange(index, 'example', e.target.value)}
										/>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</div>
				))}
			</div>

			{/* Removed the bottom save button as requested */}
		</div>
	);
};

export default PostCallAnalysis;
